<i18n>
{
    "uk": {
        "from": "від",
        "project": "проєкт",
        "hour": "год",
        "services": "{n} послуг | {n} послуга | {n} послуги | {n} послуг"
    },
    "en": {
        "from": "from",
        "project": "project",
        "hour": "hour",
        "services": "{n} services | {n} service | {n} services"
    }
}
</i18n>

<template>
    <div>
        <div
            v-for="(category, i) in categories"
            :key="`service-${category.id}-${sugar}`"
        >

            <div v-if="category.services && category.services.length">
                <CollapseButton
                    class="d-flex align-items-center"
                    :target="`collapse-service-${category.id}`"
                >
                    <WebpPicture 
                        :src="category.icon" 
                        :alt="category.name" 
                        width="40"
                        height="40"
                        imgClass="d-block mr-1"
                    />

                    <div class="pr-2">
                        <div class="fw-500">{{category.name}}</div>
                        <div class="f-semismall color-gray-0 mt-h lh-1">
                            {{ $tc('companyPage.serviceTypes', category.services.length) }} 
                            <span v-if="category.pricePerProject">• {{ $t('from') }} ${{ priceFormat(category.pricePerProject) }}/{{ $t('project') }}</span>
                            <span v-else-if="category.pricePerHour">• {{ $t('from') }} ${{ priceFormat(category.pricePerHour) }}/{{ $t('hour') }}</span>
                        </div>
                    </div>

                    <span class="icon-ic-arrow-simple color-gray-2 ml-auto l-service-icon"/>
                </CollapseButton>

                <Collapse
                    :id="`collapse-service-${category.id}`"
                    :collapse="true"
                    class="fade-effect"
                >
                    <div class="pt-1">
                        <div class="p-2 polished bg-color-graish-white-2">
                            <div
                                v-for="(child, j) in category.services"
                                :key="`child-${child.service.id}-${j}-${sugar}`"
                                class="uniq"
                            >
                                <component 
                                    :is="noContacts ? 'div': 'button'"
                                    class="p-1 d-sm-flex justify-content-between w-100" 
                                    :class="{ 'interactive-service': !noContacts }"
                                    @click="showPanel(child.service)"
                                >
                                    <div>{{child.service.name}}</div>

                                    <div
                                        class="color-gray-0 ml-sm-2 flex-shrink-0"
                                    >
                                        <span v-if="child.pricePerProject">
                                            {{ $t('from') }} ${{ priceFormat(child.pricePerProject) }}
                                        </span>
                                        <span v-else-if="child.pricePerHour">{{ $t('from') }} ${{priceFormat(child.pricePerHour)}}/{{$t('time.h')}}</span>
                                    </div>
                                </component>
                                <div class="delimiter my-1" v-if="j != category.services.length - 1" />
                            </div>
                        </div>
                    </div>
                </Collapse>

                <div
                    v-if="i != categories.length - 1"
                    class="delimiter my-1"
                />
            </div>

            <component 
                :is="noContacts ? 'div': 'button'"
                v-else
                class="row l-servise"
                @click="showPanel(category.service)"
                :class="{ 'interactive-service': !noContacts }"
            >
                <div class="col-6 d-flex align-items-center">
                    <span class="icon-ic-tick color-gray-2 mr-1 l-service-icon"/>
                    {{category.service.name}}
                </div>
                <div class="col-6 ta-right color-gray-0">
                    <span v-if="category.pricePerProject">
                        {{ $t('from') }} ${{ priceFormat(category.pricePerProject) }}/{{ $t('project') }}
                    </span>
                    <span v-else-if="category.pricePerHour">
                        {{ $t('from') }} ${{ priceFormat(category.pricePerHour) }}/{{ $t('hour') }}
                    </span>
                </div>
                <div class="col-12" v-if="i != categories.length - 1">
                    <div class="delimiter my-1"></div>
                </div>
            </component>

        </div>

        <client-only>
        <Panel
            modal-name="serviceInfo"
            side="right"
            :def-title="''"
            v-if="!noContacts"
        >
            <div
                v-if="currentService"
                class="pl-3 pr-2 pb-3"
            >
                {{currentService.description}}

                <div class="fw-700 mt-4 mb-2">
                    {{$t('companyPage.serviceContacts')}}
                </div>

                <a
                    v-if="profile && profile.site"
                    :href="wrapLink(profile.site)"
                    target="_blank"
                    class="d-link d-flex align-items-center mb-h"
                >
                    <span class="icon-ic-link color-gray-3 s-2 mr-h"/> {{profile.site}}
                </a>
                <div
                    v-if="profile && profile.email"
                    class="d-link d-flex align-items-center mb-h"
                    :href="`mailto:${profile.email}`"
                >
                    <span class="icon-ic-letter color-gray-3 s-2 mr-h"/> {{profile.email}}
                </div>
                <div
                    v-if="profile && profile.phone"
                    class="d-link d-flex align-items-center"
                    :href="`tel:${profile.phone}`"
                >
                    <span class="icon-ic-phone color-gray-3 s-2 mr-h"/> {{profile.phone}}
                </div>
            </div>
        </Panel>
        </client-only>

    </div>
</template>

<script>
import Collapse   from '~/components/collapse/Collapse.vue';
import WebpPicture  from '~/components/WebpPicture.vue';
import CollapseButton from '~/components/collapse/Button.vue';

import numberFormat from '~/utilities/numberFormat.js';

export default {
    name: 'Services',

    components: {
        Collapse,
        WebpPicture,
        CollapseButton,
        Panel: () => import('~/components/panels/CasesPanel.vue')
    },

    props: ['services', 'profile', 'noContacts'],

    data () {
        return {
            currentService: null,
            sugar: new Date().getTime()
        }
    },

    computed: {
        locale () {
            return this.$i18n.locale;
        },

        categories () {
            let clone =  this.$structuredClone(this.services);
            let categories = [];

            clone.forEach(cs => {
                if (!cs.service) return;

                cs.service.name = cs.service.name[this.locale];

                if (!cs.service.category) {
                    return categories.push(cs);
                }

                let index = categories.findIndex(o => o.id == cs.service.category.id);

                // Create new category
                if (index < 0) {
                    cs.service.category.name = cs.service.category.name[this.locale];
                    cs.service.category.services = [cs];

                    if (cs.pricePerProject) cs.service.category.pricePerProject = cs.pricePerProject;
                    if (cs.pricePerHour) cs.service.category.pricePerHour = cs.pricePerHour;

                    categories.push(cs.service.category);

                // Push to exists category
                } else {
                    categories[index].services.push(cs);

                    if (cs.pricePerProject && 
                        (
                            (!categories[index].pricePerProject && cs.pricePerProject) || 
                            categories[index].pricePerProject > cs.pricePerProject
                        )
                    ) {
                        categories[index].pricePerProject = cs.pricePerProject
                    }

                    if (cs.pricePerHour &&
                        (
                            (!categories[index].pricePerHour && cs.pricePerHour) ||
                            categories[index].pricePerHour > cs.pricePerHour
                        )
                    ) {
                        categories[index].pricePerHour = cs.pricePerHour
                    }
                }
            });

            return categories;
        }
    },


    methods: {
        wrapLink (url, scheme = 'https') {
            if (url.match(/^[a-zA-Z]+:\/\//)) {
                return url;
            }

            return scheme + '://' + url;
        },

        priceFormat (x) {
            return numberFormat(x, this.locale);
        },

        showPanel (service) {
            if (this.noContacts) return;

            this.currentService = service;


            this.$root.$emit('change-panel-title', {
                name: 'serviceInfo',
                title: this.currentService.name
            });

            this.$modalityStore.openModal({
                type: 'drawer',
                name: 'serviceInfo',
                title: this.currentService.name
            });
        }
    }
}
</script>

<style lang="scss" scoped>
    .collapse-button {
        padding: 8px;
        width: 100%;
        background-color: transparent;
        border-radius: 8px;
        transition: background-color .1s linear,
                    outline .1s linear;

        &:hover {
            background-color: var(--color-cases-blue-dim);
        }

        &:focus-visible {
            outline: 3px solid var(--color-cases-cadmium-yellow);
        }
    }

    .l-service-icon {
        width: 16px;
        flex-basis: 16px;
        flex-shrink: 0;

        &.icon-ic-tick {font-size: 10px;}
        &.icon-ic-arrow-simple {
            font-size: 18px;
            transform: rotate(180deg);
            transition: transform .35s ease;
        }
    }

    .collapsed {
        .icon-ic-arrow-simple {
            transform: rotate(0deg);
        }
    }

    .interactive-service {
        background-color: transparent;
        outline: none;
        border: none;
        cursor: pointer;
        transition: background-color .15s ease, outline .1s linear;
        width: 100%;
        border-radius: 8px;
        text-align: left;

        &:hover {
            background-color: var(--color-cases-blue-dim);
        }

        &:focus-visible {
            outline: 3px solid var(--color-cases-cadmium-yellow);
        }
    }

    .s-2 {
        width: 16px;
        height: 16px;
        display: flex;
        justify-content: center;
        align-items: center;

        font-size: 14px;

        &.icon-ic-letter {
            font-size: 10px;
        }

        &.icon-ic-link {
            font-size: 12px;
        }
    }

    .bg-color-graish-white-2 {
        background-color: var(--color-cases-graish-white-2);
    }
</style>
